"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import { ArrowLeft, ChevronRight, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

export type Breadcrumb = {
  label: string;
  href: string | null;
  maxCharacters?: number;
};

function truncateLabel(label: string, max?: number) {
  if (!max || label.length <= max) return label;
  return label.slice(0, max) + "…";
}

type NavigationBreadcrumbsProps = {
  breadcrumbs: Breadcrumb[];
};

export default function NavigationBreadcrumbs({ breadcrumbs }: NavigationBreadcrumbsProps) {
  const router = useRouter();
  const [canGoBack, setCanGoBack] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setCanGoBack(window.history.length > 1);
    }
  }, []);

  return (
    <div className="sticky top-0 z-10 flex items-center gap-3 border-b bg-background/100 backdrop-blur px-4 md:px-6 min-h-12 py-2">
      {canGoBack && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => router.back()}
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
      )}
      <nav className="flex flex-wrap items-center text-sm text-muted-foreground min-w-0 gap-y-1">
        <Link href="/" className="hover:text-foreground transition shrink-0">
          <Home className="h-4 w-4" />
        </Link>
        {breadcrumbs.map((crumb, index) => (
          <span key={index} className="flex items-center">
            <ChevronRight className="h-4 w-4 mx-1 text-muted-foreground/50 shrink-0" />
            {crumb.href ? (
              <Link href={crumb.href} className="hover:text-foreground transition" title={crumb.maxCharacters && crumb.label.length > crumb.maxCharacters ? crumb.label : undefined}>
                {truncateLabel(crumb.label, crumb.maxCharacters)}
              </Link>
            ) : (
              <span className="text-foreground font-medium" title={crumb.maxCharacters && crumb.label.length > crumb.maxCharacters ? crumb.label : undefined}>
                {truncateLabel(crumb.label, crumb.maxCharacters)}
              </span>
            )}
          </span>
        ))}
      </nav>
    </div>
  );
}
